import { FC, useState } from 'react';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { getSpecSelectedOptionsById } from '../../fetch';
import { ISpecDetailResponse } from '../../models';
import { Breadcrumbs, Loader, Page, PageTitleWithBack } from '../../components';
import { SelectedOptionsTable } from '../development/manage-specs/SelectedOptionsTable';
import { useLocation, useParams } from 'react-router-dom';
import { kebab } from 'case';
import { Box, Typography } from '@mui/material';

interface IBuildSelectedOptionsDetail {
  path?: string;
  backText?: string;
}

export const BuildSelectedOptionsDetail: FC<IBuildSelectedOptionsDetail> = ({ path, backText }) => {
  const { neighborhoodId, lot }: { neighborhoodId: string; lot: string } = useParams();
  const location = useLocation();

  const [hasError, setHasError] = useState<boolean>(false);

  const [neighborhoodName, setNeighborhoodName] = useState<string | undefined>(undefined);
  const [lotNumber, setLotNumber] = useState<string | undefined>(undefined);
  const [neighborhoodSeries, setNeighborhoodSeries] = useState<string | undefined>(undefined);
  const [planDescription, setPlanDescription] = useState<string | undefined>(undefined);

  const generateBreadcrumb = (url: string, title: string) => {
    let customBreadcrumbPath = url
      .split('/')
      .filter(x => x)
      .slice(0, -2)
      .join('/');
    const endPath = kebab(title);
    customBreadcrumbPath = customBreadcrumbPath + '/' + endPath;
    return customBreadcrumbPath;
  };

  const subHeaderContent = () => {
    return (
      <Box marginBottom={1}>
        {planDescription && <Typography variant="h6">Plan: {planDescription}</Typography>}
        {neighborhoodSeries && <Typography variant="h6">Series: {neighborhoodSeries}</Typography>}
      </Box>
    );
  };

  // context
  const { enqueueSnackbar } = useSnackbar();

  // queries
  const { isLoading: isLoadingSelectedOptions, data: selectedOptionsData } =
    useQuery<ISpecDetailResponse>(
      ['selectedOptions', neighborhoodId, lot],
      () => getSpecSelectedOptionsById({ neighborhoodId: neighborhoodId, lotNumber: lot }),
      {
        // The query will not execute until the neighborhoodId and lot exists
        enabled: !!neighborhoodId && !!lot,
        // whenever neighborhoodId changes, refetch the data
        notifyOnChangeProps: 'tracked',
        onSuccess: (d: any) => {
          // make sure the call doesn't refresh again if we hit an error
          if (!hasError && d?.Detail) {
            enqueueSnackbar(d?.Detail, {
              variant: 'error'
            });
            return setHasError(true);
          }
          setNeighborhoodName(d.neighborhoodName);
          setLotNumber(d.lotNumber);
          setNeighborhoodSeries(d.series);
          setPlanDescription(d.planDescription);
          return d;
        },
        onError: (e: any) => {
          // make sure the call doesn't refresh again if we hit an error
          if (!hasError) {
            enqueueSnackbar(
              e?.response?.data?.Detail || `Error loading selected options, please try again.`,
              {
                variant: 'error'
              }
            );
            setHasError(true);
          }
        },
        staleTime: Infinity
      }
    );
  // initialize the data
  const compiledOptions = [];
  if (selectedOptionsData?.selectedOptions) {
    selectedOptionsData?.selectedOptions.forEach(option => {
      compiledOptions.push(option);
    });
  }
  if (selectedOptionsData?.addendumAOptions) {
    selectedOptionsData?.addendumAOptions.forEach(option => {
      compiledOptions.push(option);
    });
  }

  return (
    <>
      <Page
        showBreadcrumbs={false}
        title="Build Selected Options"
        showSideBar={false}
        printOrientation="landscape"
      >
        <Breadcrumbs
          customPath={generateBreadcrumb(
            location.pathname,
            neighborhoodName && lotNumber
              ? `${neighborhoodName} - Lot ${lotNumber}`
              : neighborhoodId
          )}
        />
        <PageTitleWithBack
          backText={backText ?? 'Back to Build Selected Options'}
          backUrl={path ?? '/documentation/build-selected-options'}
          title="Build Selected Options"
        />

        {isLoadingSelectedOptions && <Loader type="inline" position="centered" />}
        {!isLoadingSelectedOptions && compiledOptions && neighborhoodId && lot && (
          <SelectedOptionsTable
            selectedOptions={compiledOptions}
            subHeaderContent={subHeaderContent()}
            title={
              neighborhoodName && lotNumber ? `${neighborhoodName} - Lot ${lotNumber}` : undefined
            }
            showPriceColumn={false}
          />
        )}
      </Page>
    </>
  );
};
